import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useState, useEffect, ReactElement } from 'react'
import { useIntl, defineMessages } from 'react-intl'

import { Autocomplete } from 'components/v2/Autocomplete'
import { normalize } from 'utils/string'
import { GTVehicle } from 'gql/types.generated'
import { IAutocompleteProps } from 'components/v2/Autocomplete/types'

const GET_ROUTE_VEHICLES = gql`
  query GetRouteVehicles($routeId: ID!) {
    routeVehicles(input: { routeID: $routeId }) {
      id
      name
      template {
        capacity
      }
    }
  }
`

const messages = defineMessages({
  error: {
    id: 'vehicles.autocomplete.error',
    defaultMessage: 'There was an issue while loading your vehicles'
  },
  empty: {
    id: 'vehicles.autocomplete.empty',
    defaultMessage: 'No vehicles found'
  }
})

const filterVehicles = (vehicles: GTVehicle[], pattern: string): GTVehicle[] => {
  vehicles = vehicles || []
  return vehicles.filter((vehicle: TSFixMe): TSFixMe => {
    return normalize(vehicle.name).indexOf(normalize(pattern)) > -1
  })
}

const getItemLabel = (vehicle: GTVehicle): string => {
  if (vehicle.template) {
    return `${vehicle.name} (${vehicle.template.capacity})`
  }

  return vehicle.name
}

interface IRouteVehicleAutocompleteProps extends Omit<IAutocompleteProps, 'items' | 'getItemLabel' | 'onChange'> {
  routeId: string
  onChange: (vehicle: { id: string; name: string }) => void
}

export const RouteVehicleAutocomplete = ({
  routeId,
  onChange,
  ...props
}: IRouteVehicleAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const {
    data: routeVehiclesData,
    loading: routeVehiclesLoading,
    error: routeVehiclesError,
    refetch,
    networkStatus
  } = useQuery(GET_ROUTE_VEHICLES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !routeId,
    variables: {
      routeId
    }
  })

  useEffect(() => {
    if (!routeId) {
      refetch()
    }
  }, [])

  const vehiclesItems = routeVehiclesData?.routeVehicles
  const shownItems = filterVehicles(vehiclesItems, pattern)

  const handleChange = (vehicle: TSFixMe, { inputValue = '' } = {}): void => {
    setPattern(inputValue)

    if (!isEmpty(vehicle)) {
      onChange(vehicle)
    } else {
      onChange({
        id: '',
        name: ''
      })
    }
  }

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={getItemLabel}
      loading={routeVehiclesLoading || networkStatus === NetworkStatus.refetch}
      error={routeVehiclesError}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
    />
  )
}
